/**
 * 饮食减肥方案 - 独立页面
 * 来源：南方医科大学深圳医院营养科
 * 肠道微生态 + 限能量均衡饮食减肥方案 (标准版)
 */
import React from 'react';
import { useNavigate } from 'react-router-dom';
import {
  ChevronLeft, Utensils, Apple, Activity, AlertCircle, Pill, Stethoscope
} from 'lucide-react';
import './DietPlan.css';

function DietPlan() {
  const navigate = useNavigate();

  return (
    <div className="diet-plan-page">
      <header className="diet-plan-header">
        <button
          type="button"
          className="diet-plan-back"
          onClick={() => navigate('/health')}
          aria-label="返回"
        >
          <ChevronLeft size={24} />
        </button>
        <h1 className="diet-plan-title">饮食减肥方案</h1>
      </header>

      <main className="diet-plan-main">
        <section className="diet-plan-section">
          <h2 className="diet-plan-section-title">
            <Utensils size={20} />
            饮食原则
          </h2>
          <p className="diet-plan-source">
            南方医科大学深圳医院营养科 · 肠道微生态+限能量均衡饮食 (标准版)
          </p>
          <ul className="diet-plan-list">
            <li>食物重量为食物生重（烹调前的重量）</li>
            <li>限制热量摄入，以健康适当的体脂肪为目标</li>
            <li>每日饮水量 2000–2100ml，分次饮用</li>
            <li>碳酸氢钠片用温开水溶解后，1 天内慢慢喝完</li>
            <li>按时服用医嘱药物及营养补充剂</li>
            <li>控制精制碳水，优先选择粗粮、蔬菜、优质蛋白</li>
          </ul>
        </section>

        <section className="diet-plan-section">
          <h2 className="diet-plan-section-title">
            <Apple size={20} />
            食谱示例
          </h2>

          <div className="diet-plan-meal">
            <h3>早餐 7:30</h3>
            <ul>
              <li>赛能纽艾达</li>
              <li>代餐棒半包（1 条）</li>
              <li>主食/蛋白质（任选其一）：150g 嫩豆腐 或 65g 豆干</li>
              <li>鸡蛋 1 个（60g）</li>
              <li>蔬菜（任选其一）：番茄 250g 或 青菜 250g</li>
            </ul>
          </div>

          <div className="diet-plan-meal">
            <h3>午餐 12:00</h3>
            <ul>
              <li>海洋胶原肽 1 条 + 立优安 1 条</li>
              <li>归朴代餐棒 1 包（1 条）</li>
              <li>瘦猪/牛/羊/鸡鸭肉类（去皮）50g</li>
              <li>蛋白质（任选其一）：鱼虾 100g 或 豆腐 150g</li>
              <li>青菜 250g</li>
              <li>汤类（任选其一）：青菜汤 或 藻类
                <span className="diet-plan-optional"> — 不喝汤可不喝，只喝汤不吃汤渣</span>
              </li>
              <li>本餐全部烹调用油按医嘱用量</li>
            </ul>
          </div>

          <div className="diet-plan-meal">
            <h3>晚餐 17:30</h3>
            <ul>
              <li>赛能纽艾达</li>
              <li>代餐棒半包（1 条）</li>
              <li>瘦猪/牛/羊/鸡鸭肉类 50g</li>
              <li>蛋白质（任选其一）：鱼虾 100g 或 豆腐 150g</li>
              <li>青菜 250g</li>
              <li>不含淀粉的汤类</li>
              <li>本餐全部烹调用油按医嘱用量</li>
            </ul>
          </div>
        </section>

        <section className="diet-plan-section">
          <h2 className="diet-plan-section-title">
            <Pill size={20} />
            辅助增强减肥效果营养素
          </h2>
          <ul className="diet-plan-list">
            <li>复方谷氨酰胺肠溶胶囊：2 粒/次，每日 2 次，早餐前、晚餐前</li>
            <li>三联活菌胶囊：2 粒/次，每日 2 次，早餐后、晚餐后（需放冰箱保存）</li>
            <li>归朴维生素：1 粒/次，午餐后</li>
            <li>碳酸氢钠片：3 粒/次，用 2000–2100ml 温开水溶解，1 天慢慢喝完</li>
            <li>碳酸钙 D3：1 片/次，午餐后</li>
            <li>知因果素（萝卜硫素）：1 粒/次，每日 3 次，早/中/晚餐后</li>
          </ul>
        </section>

        <section className="diet-plan-section">
          <h2 className="diet-plan-section-title">
            <Activity size={20} />
            运动处方
          </h2>
          <p className="diet-plan-subsection">身体允许情况下执行：</p>
          <ul className="diet-plan-list">
            <li>跳绳：每组跳 3 分钟（跳 1 分钟休 1 分钟再跳 1 分钟），
              每天 5–10 组，共 15–30 分钟；以肩关节为中心打开大臂跳绳</li>
            <li>划船、自由泳等上肢抗阻运动：每次 15–30 分钟</li>
            <li>随诊：每两周门诊随诊</li>
          </ul>
        </section>

        <section className="diet-plan-section">
          <h2 className="diet-plan-section-title">
            <Stethoscope size={20} />
            减肥前后检测指标
          </h2>
          <ul className="diet-plan-list">
            <li>血常规 + 尿常规 + 镜检 + 尿微量蛋白三项测定 + 大便常规 + 潜血试验</li>
            <li>肝功能六项 + 肾功能 4 项 + 血脂四项或血脂八项</li>
            <li>B 超：颈动脉超声，肝、胆、胰腺（空腹）超声</li>
            <li>肠道屏障功能：二胺氧化酶、D-乳酸、细胞内毒素</li>
            <li>人体成分分析：体脂测定</li>
          </ul>
        </section>

        <section className="diet-plan-section diet-plan-notice">
          <h2 className="diet-plan-section-title">
            <AlertCircle size={20} />
            注意事项
          </h2>
          <ul className="diet-plan-list">
            <li>空腹测量体重，每日早晨打卡</li>
            <li>尿酮自第三天起每日监测</li>
            <li>特殊医疗用途食品须在营养医师或临床医师指导下根据适应症使用</li>
            <li>科学合理使用有助于康复、降低总体医疗费用；具体服用方法以病历/食品包装为准</li>
            <li>涉及医学诊断请咨询专业医生</li>
          </ul>
        </section>
      </main>
    </div>
  );
}

export default DietPlan;
